import React, { useState, useEffect } from 'react';
import { MorningRoutine, RoutineStep, StepCondition, StepAction, SmartDevice } from './interfaces/types';
import { Plus, X, ChevronDown, ChevronUp, Save, Trash2, Edit2, Clock, Zap } from 'lucide-react';

interface RoutineBuilderProps {
  devices: SmartDevice[];
  existingRoutines: MorningRoutine[];
  onSaveRoutine: (routine: MorningRoutine) => void;
  onDeleteRoutine: (routineId: string) => void;
  onTestRoutine: (routineId: string) => void;
}

// Helper function to generate unique IDs
const generateId = (prefix: string): string => {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

const RoutineBuilder: React.FC<RoutineBuilderProps> = ({
  devices,
  existingRoutines,
  onSaveRoutine,
  onDeleteRoutine,
  onTestRoutine
}) => {
  const [editMode, setEditMode] = useState<'new' | 'edit' | null>(null);
  const [selectedRoutineId, setSelectedRoutineId] = useState<string | null>(null);
  const [expandedRoutineId, setExpandedRoutineId] = useState<string | null>(null);
  
  // Current routine being edited
  const [currentRoutine, setCurrentRoutine] = useState<MorningRoutine>({
    id: '',
    name: '',
    description: '',
    enabled: true,
    steps: [],
    defaultStartTime: { hour: 7, minute: 0 },
    daysOfWeek: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
    creator: 'User',
    createdAt: new Date(),
    lastModified: new Date()
  });
  
  // Currently editing step
  const [currentStep, setCurrentStep] = useState<RoutineStep | null>(null);
  const [stepEditIndex, setStepEditIndex] = useState<number | null>(null);
  
  // Selected days of week
  const [selectedDays, setSelectedDays] = useState<{
    MON: boolean;
    TUE: boolean;
    WED: boolean;
    THU: boolean;
    FRI: boolean;
    SAT: boolean;
    SUN: boolean;
  }>({
    MON: true,
    TUE: true,
    WED: true,
    THU: true,
    FRI: true,
    SAT: false,
    SUN: false
  });
  
  // Initialize routine creation
  const handleNewRoutine = () => {
    setCurrentRoutine({
      id: generateId('routine'),
      name: '',
      description: '',
      enabled: true,
      steps: [],
      defaultStartTime: { hour: 7, minute: 0 },
      daysOfWeek: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
      creator: 'User',
      createdAt: new Date(),
      lastModified: new Date()
    });
    
    // Reset selected days
    setSelectedDays({
      MON: true,
      TUE: true,
      WED: true,
      THU: true,
      FRI: true,
      SAT: false,
      SUN: false
    });
    
    setEditMode('new');
    setCurrentStep(null);
    setStepEditIndex(null);
  };
  
  // Edit an existing routine
  const handleEditRoutine = (routineId: string) => {
    const routineToEdit = existingRoutines.find(r => r.id === routineId);
    if (!routineToEdit) return;
    
    setCurrentRoutine({ ...routineToEdit });
    
    // Set selected days based on routine
    const days = {
      MON: false,
      TUE: false,
      WED: false,
      THU: false,
      FRI: false,
      SAT: false,
      SUN: false
    };
    
    routineToEdit.daysOfWeek.forEach(day => {
      days[day] = true;
    });
    
    setSelectedDays(days);
    setEditMode('edit');
    setSelectedRoutineId(routineId);
    setCurrentStep(null);
    setStepEditIndex(null);
  };
  
  // Toggle routine expansion to show steps
  const toggleRoutineExpansion = (routineId: string) => {
    if (expandedRoutineId === routineId) {
      setExpandedRoutineId(null);
    } else {
      setExpandedRoutineId(routineId);
    }
  };
  
  // Create a new step
  const handleNewStep = () => {
    const newStep: RoutineStep = {
      id: generateId('step'),
      name: '',
      description: '',
      condition: {
        type: 'TIME',
        timeWindow: {
          start: { hour: 7, minute: 0 },
          end: { hour: 9, minute: 0 }
        }
      },
      actions: [{
        deviceId: devices.length > 0 ? devices[0].id : '',
        command: {
          commandType: 'ON'
        }
      }],
      isOptional: false
    };
    
    setCurrentStep(newStep);
    setStepEditIndex(null);
  };
  
  // Edit an existing step
  const handleEditStep = (index: number) => {
    if (index >= 0 && index < currentRoutine.steps.length) {
      setCurrentStep({ ...currentRoutine.steps[index] });
      setStepEditIndex(index);
    }
  };
  
  // Save the current step to the routine
  const handleSaveStep = () => {
    if (!currentStep) return;
    
    // Update steps array
    let updatedSteps = [...currentRoutine.steps];
    
    if (stepEditIndex !== null) {
      // Update existing step
      updatedSteps[stepEditIndex] = currentStep;
    } else {
      // Add new step
      updatedSteps.push(currentStep);
    }
    
    // Update routine
    setCurrentRoutine({
      ...currentRoutine,
      steps: updatedSteps
    });
    
    // Reset step editing
    setCurrentStep(null);
    setStepEditIndex(null);
  };
  
  // Delete a step from the routine
  const handleDeleteStep = (index: number) => {
    if (index >= 0 && index < currentRoutine.steps.length) {
      const updatedSteps = [...currentRoutine.steps];
      updatedSteps.splice(index, 1);
      
      setCurrentRoutine({
        ...currentRoutine,
        steps: updatedSteps
      });
    }
  };
  
  // Update current routine's days of week when selectedDays changes
  useEffect(() => {
    if (editMode) {
      const selectedDaysList = (Object.entries(selectedDays)
        .filter(([_, isSelected]) => isSelected)
        .map(([day]) => day)) as ('MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN')[];
      
      setCurrentRoutine(prev => ({
        ...prev,
        daysOfWeek: selectedDaysList
      }));
    }
  }, [selectedDays, editMode]);
  
  // Save the routine
  const handleSaveRoutine = () => {
    if (!currentRoutine.name) {
      alert('Please enter a name for the routine');
      return;
    }
    
    if (currentRoutine.steps.length === 0) {
      alert('Please add at least one step to the routine');
      return;
    }
    
    // Update modification time
    const routineToSave = {
      ...currentRoutine,
      lastModified: new Date()
    };
    
    onSaveRoutine(routineToSave);
    
    // Reset form
    setEditMode(null);
    setSelectedRoutineId(null);
    setCurrentStep(null);
    setStepEditIndex(null);
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditMode(null);
    setSelectedRoutineId(null);
    setCurrentStep(null);
    setStepEditIndex(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Routine Builder</h3>
        
        {!editMode && (
          <button
            onClick={handleNewRoutine}
            className="flex items-center px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
          >
            <Plus size={16} className="mr-1" />
            New Routine
          </button>
        )}
      </div>
      
      {/* Existing Routines List */}
      {!editMode && (
        <div>
          <h4 className="text-sm font-medium mb-2">Your Routines</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {existingRoutines.length === 0 ? (
              <div className="text-gray-500 text-sm text-center p-4 border border-dashed rounded-lg">
                No routines created yet. Click "New Routine" to get started.
              </div>
            ) : (
              existingRoutines.map(routine => (
                <div key={routine.id} className="border rounded-lg overflow-hidden">
                  <div className="p-3 bg-gray-50 flex justify-between items-center">
                    <div>
                      <div className="font-medium">{routine.name}</div>
                      <div className="text-xs text-gray-500">
                        {routine.defaultStartTime.hour.toString().padStart(2, '0')}:
                        {routine.defaultStartTime.minute.toString().padStart(2, '0')} | 
                        {routine.daysOfWeek.join(', ')}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onTestRoutine(routine.id)}
                        className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                        title="Test Routine"
                      >
                        <Zap size={16} />
                      </button>
                      <button
                        onClick={() => handleEditRoutine(routine.id)}
                        className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        title="Edit Routine"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete the routine "${routine.name}"?`)) {
                            onDeleteRoutine(routine.id);
                          }
                        }}
                        className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                        title="Delete Routine"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button
                        onClick={() => toggleRoutineExpansion(routine.id)}
                        className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                        title={expandedRoutineId === routine.id ? 'Collapse' : 'Expand'}
                      >
                        {expandedRoutineId === routine.id ? 
                          <ChevronUp size={16} /> : 
                          <ChevronDown size={16} />
                        }
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Steps View */}
                  {expandedRoutineId === routine.id && (
                    <div className="p-3 border-t">
                      <div className="text-xs font-medium mb-2">Steps:</div>
                      {routine.steps.length === 0 ? (
                        <div className="text-xs text-gray-500 italic">No steps defined</div>
                      ) : (
                        <div className="space-y-2">
                          {routine.steps.map((step, index) => (
                            <div key={step.id} className="bg-gray-50 p-2 rounded text-xs">
                              <div className="font-medium">{index + 1}. {step.name}</div>
                              {step.description && (
                                <div className="text-gray-600 mt-0.5">{step.description}</div>
                              )}
                              <div className="flex mt-1">
                                <div className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs">
                                  {step.condition.type}
                                </div>
                                {step.isOptional && (
                                  <div className="ml-1 bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded text-xs">
                                    Optional
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* Routine Edit Form */}
      {editMode && (
        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-medium mb-4">
            {editMode === 'new' ? 'Create New Routine' : 'Edit Routine'}
          </h4>
          
          <div className="space-y-4">
            {/* Routine Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routine Name
              </label>
              <input
                type="text"
                className="w-full border rounded-lg p-2"
                value={currentRoutine.name}
                onChange={e => setCurrentRoutine({ ...currentRoutine, name: e.target.value })}
                placeholder="e.g., Morning Wake-up"
              />
            </div>
            
            {/* Routine Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                className="w-full border rounded-lg p-2"
                value={currentRoutine.description || ''}
                onChange={e => setCurrentRoutine({ ...currentRoutine, description: e.target.value })}
                placeholder="Describe what this routine does"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Start Time
                </label>
                <div className="flex">
                  <input
                    type="number"
                    className="w-1/2 border-r-0 rounded-l-lg p-2"
                    min={0}
                    max={23}
                    value={currentRoutine.defaultStartTime.hour}
                    onChange={e => setCurrentRoutine({
                      ...currentRoutine,
                      defaultStartTime: {
                        ...currentRoutine.defaultStartTime,
                        hour: parseInt(e.target.value) || 0
                      }
                    })}
                  />
                  <input
                    type="number"
                    className="w-1/2 border-l-0 rounded-r-lg p-2"
                    min={0}
                    max={59}
                    value={currentRoutine.defaultStartTime.minute}
                    onChange={e => setCurrentRoutine({
                      ...currentRoutine,
                      defaultStartTime: {
                        ...currentRoutine.defaultStartTime,
                        minute: parseInt(e.target.value) || 0
                      }
                    })}
                  />
                </div>
              </div>
              
              {/* Enabled Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={currentRoutine.enabled ? 'enabled' : 'disabled'}
                  onChange={e => setCurrentRoutine({
                    ...currentRoutine,
                    enabled: e.target.value === 'enabled'
                  })}
                >
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            </div>
            
            {/* Days of Week */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Run on Days
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(selectedDays).map(([day, isSelected]) => (
                  <button
                    key={day}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isSelected ?
                        'bg-blue-100 text-blue-800 border-blue-300' :
                        'bg-gray-100 text-gray-800 border-gray-300'
                    } border`}
                    onClick={() => setSelectedDays({
                      ...selectedDays,
                      [day]: !isSelected
                    })}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Steps section will be added in Part 3 */}
            
            {/* Form Actions */}
            <div className="flex justify-between pt-4 border-t">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRoutine}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
              >
                <Save size={16} className="mr-1" />
                Save Routine
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutineBuilder; 